<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px;">
      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        label="Estado"
        dense
        outlined
        hide-details
        style="max-width: 260px;"
        @change="fetchData"
      />
    </div>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loadingData"
      :items-per-page="20"
      class="elevation-1"
      :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
      :no-data-text="emptyText"
      @click:row="openDetail"
    >
      <template v-slot:item.treatmentPurpose="{ item }">
        {{ item.treatmentPurpose }}
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusMeta(item.status).color" class="white--text font-weight-bold">
          {{ statusMeta(item.status).label }}
        </v-chip>
      </template>

      <template v-slot:item.deadlineAt="{ item }">
        <span :class="deadlineMeta(item).class">
          {{ effectiveDeadline(item) | date }} — {{ deadlineMeta(item).label }}
        </span>
      </template>

      <template v-slot:item.blockRequested="{ item }">
        <v-icon v-if="item.blockRequested" small color="orange darken-2">mdi-lock-alert-outline</v-icon>
        <span v-else class="grey--text">—</span>
      </template>
    </v-data-table>

    <v-dialog v-model="detailDialog" max-width="720" @keydown.esc="detailDialog = false">
      <OppositionRequestReviewModal
        v-if="selectedItem"
        :request="selectedItem"
        @close="detailDialog = false"
        @resolved="onResolved"
      />
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import OppositionRequestsService from '@/services/opposition-requests.service';
import OppositionRequestReviewModal from './OppositionRequestReviewModal.vue';
import { utilsMixin } from '@/mixins/utilsMixin';
import { OPPOSITION_REQUEST_STATUS } from '@/utils/constants';

const STATUS_META = {
  [OPPOSITION_REQUEST_STATUS.RECEIVED]: { label: 'Recibida', color: 'blue-grey' },
  [OPPOSITION_REQUEST_STATUS.PENDING_MANUAL_REVIEW]: { label: 'Pendiente de revisión', color: 'orange' },
  [OPPOSITION_REQUEST_STATUS.ACCEPTED]: { label: 'Aceptada', color: 'green' },
  [OPPOSITION_REQUEST_STATUS.REJECTED]: { label: 'Rechazada', color: 'red' }
};

// Aviso de "próxima a vencer" cuando quedan 3 días o menos — no viene del spec como
// un número exacto, es un valor razonable por defecto (plan.md/spec.md § Assumptions).
const SOON_THRESHOLD_DAYS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: 'OppositionRequestsTable',
  components: { OppositionRequestReviewModal },
  mixins: [utilsMixin],

  data() {
    return {
      loadingData: false,
      items: [],
      statusFilter: OPPOSITION_REQUEST_STATUS.PENDING_MANUAL_REVIEW,
      detailDialog: false,
      selectedItem: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'green'
    };
  },

  computed: {
    statusOptions() {
      return [
        { text: 'Pendientes de revisión', value: OPPOSITION_REQUEST_STATUS.PENDING_MANUAL_REVIEW },
        { text: 'Recibidas', value: OPPOSITION_REQUEST_STATUS.RECEIVED },
        { text: 'Aceptadas', value: OPPOSITION_REQUEST_STATUS.ACCEPTED },
        { text: 'Rechazadas', value: OPPOSITION_REQUEST_STATUS.REJECTED }
      ];
    },

    headers() {
      return [
        { text: 'Motivo del tratamiento', value: 'treatmentPurpose' },
        { text: 'Estado', value: 'status' },
        { text: 'Plazo', value: 'deadlineAt' },
        { text: 'Bloqueo solicitado', value: 'blockRequested' }
      ];
    },

    emptyText() {
      if (this.loadingData) return 'Cargando...';
      if (this.statusFilter === OPPOSITION_REQUEST_STATUS.PENDING_MANUAL_REVIEW) {
        return 'No hay solicitudes pendientes de revisión manual.';
      }
      return 'No hay solicitudes en este estado.';
    }
  },

  created() {
    this.fetchData();
  },

  methods: {
    statusMeta(status) {
      return STATUS_META[status] || { label: status, color: 'grey' };
    },

    effectiveDeadline(item) {
      return item.extendedDeadlineAt || item.deadlineAt;
    },

    deadlineMeta(item) {
      if (item.status !== OPPOSITION_REQUEST_STATUS.PENDING_MANUAL_REVIEW) {
        return { label: '', class: '' };
      }
      const deadline = new Date(this.effectiveDeadline(item));
      const daysLeft = Math.ceil((deadline.getTime() - Date.now()) / DAY_MS);
      if (daysLeft < 0) return { label: 'Vencida', class: 'red--text font-weight-bold' };
      if (daysLeft <= SOON_THRESHOLD_DAYS) return { label: 'Próxima a vencer', class: 'orange--text text--darken-2 font-weight-bold' };
      return { label: '', class: '' };
    },

    openDetail(item) {
      this.selectedItem = item;
      this.detailDialog = true;
    },

    onResolved() {
      this.detailDialog = false;
      this.showSuccessSnackbar('Decisión registrada correctamente.');
      this.fetchData();
    },

    async fetchData() {
      this.loadingData = true;
      const res = await OppositionRequestsService.list(this.statusFilter);
      if (res.status === 'ok') {
        this.items = res.data || [];
      } else {
        this.items = [];
        this.showErrorSnackbar('Ocurrió un error al cargar las solicitudes de oposición.');
      }
      this.loadingData = false;
    }
  }
};
</script>
